import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import React, { useCallback, useMemo, useState } from 'react';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { NlpGraphCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/NlpGraphDefinition';
import { NlpGraphResult } from 'libs/end-to-end/src/custom-viz/NlpGraphTypes';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { NlpGraphQueryRequest } from 'libs/end-to-end/src/nlp-graph-query-result';
import { NlpGraphQueryResult } from 'libs/end-to-end/src/nlp-graph-query-result';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { InputBox } from 'apps/sigma2-ui/src/components/ui/InputBox';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { ResponsiveBar } from '@nivo/bar';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';

export const NlpGraphComponent = ({
  observation,
  organizationId,
}: {
  observation: MyObservation;
  organizationId: string;
}) => {
  const [queryText, setQueryText] = useState<string>('');
  const [errorText, setErrorText] = useState<string | null>(null);

  const [currentNlpGraphQueryResult, setCurrentNlpGraphQueryResult] =
    useState<NlpGraphQueryResult | null>(null);

  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${observation.type.databaseId}/revisions/${observation.type.databaseRevisionId}/nlp-query`;
  }, [observation, organizationId]);

  const generatePattern = useSimpleWritePattern({
    getUrlFn,
    method: 'POST',
    canWrite: true,
    writeSchema: NlpGraphQueryRequest,
    responseSchema: NlpGraphQueryResult,
  });

  const context = useCustomViz();

  const { doViz } = context;

  const getCustomVizRequest = useCallback(
    ({
      result,
    }: {
      result: NlpGraphQueryResult;
    }): { customVizRequest: CustomVizRequest } => {
      const customVizRequest: CustomVizRequest = {
        customVizId: 'nlpGraph',
        organizationId,
        observationLocationType: observation.type,
        inputs: {
          extras: {
            query: result.query,
            xAxisName: result.xAxisName,
            yAxisName: result.yAxisName,
            xAxisElementColumn: result.xAxisElementColumn,
            graphName: result.graphName,
            valueColumn: result.valueColumn,
          },
          dropdowns: {},
        },
      };

      return {
        customVizRequest,
      };
    },
    [observation, organizationId],
  );

  const onAnalyze = useCallback(
    ({ result }: { result: NlpGraphQueryResult }) => {
      const { customVizRequest } = getCustomVizRequest({
        result,
      });

      if (!customVizRequest) {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest],
  );

  const {
    data: graphData,
    readyToDisplayDynamicContent,
    hasFailed,
  } = useMemo(() => {
    return parseCustomVizResult({
      result: context.result,
      status: context.status,
      ResultZodType: NlpGraphResult,
    });
  }, [context.result, context.status]);

  return (
    <Spacing size={'lg'} direction={'vertical'} className={'w-full h-full p-4'}>
      <InputBox
        label={'Generate a Query'}
        value={queryText}
        setValue={setQueryText}
        widthClass={'w-full'}
      />

      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            if (generatePattern.status !== 'saving' && queryText) {
              setErrorText(null);
              generatePattern.saveEntity(
                {
                  text: queryText,
                },
                (value) => {
                  setCurrentNlpGraphQueryResult(value);

                  onAnalyze({
                    result: value,
                  });
                },
                () => {
                  setErrorText('Failed to generate query');
                },
              );
            }
          },
        }}
        size={'small'}
        variant={'primary'}
        text={'Generate'}
        disabled={!queryText && generatePattern.status !== 'saving'}
      />

      {errorText && <div className="text-red-500">{errorText}</div>}
      {hasFailed && (
        <div className="text-red-500">
          Failed to load data. Please try again later.
        </div>
      )}

      {currentNlpGraphQueryResult && (
        <div>
          <h3>Generated SQL Query:</h3>
          <pre>{currentNlpGraphQueryResult.query}</pre>
        </div>
      )}

      {readyToDisplayDynamicContent && graphData && (
        <div className={'w-full h-[400px]'}>
          <ResponsiveBar
            data={graphData.dataPoints.map((d) => ({
              id: d.xName,
              value: d.y,
            }))}
            // keys={}
            groupMode="grouped"
            valueFormat={(percentage: number) => `${percentage}`}
            indexBy={'id'}
            margin={{ top: 50, right: 50, bottom: 50, left: 100 }}
            minValue={'auto'}
            maxValue={'auto'}
            borderRadius={3}
            colors={['#3460E2', '#CE106F']}
            colorBy="id"
            padding={0.25}
            innerPadding={3}
            enableGridX={false}
            axisTop={{
              legend: graphData.graphName,
              legendOffset: -20,
              legendPosition: 'middle',
              tickPadding: 10,
              tickSize: 0,
              format: () => ``,
            }}
            axisLeft={{
              legend: graphData.yAxisName,
              legendOffset: -60,
              legendPosition: 'middle',
              tickPadding: 10,
              tickSize: 0,
              format: (value) => `${value}`,
            }}
            // Hide the bottom axis.
            axisBottom={{
              tickPadding: 0,
              tickSize: 0,
              legend: graphData.xAxisName,
              legendPosition: 'middle',
              legendOffset: 40,
              format: (id) => `${id}`,
            }}
            theme={{
              text: {
                fontFamily: KARLA_FONT_FAMILY,
                fontSize: 16,
              },
              axis: {
                domain: {
                  line: {
                    stroke: '#B8BABC',
                    strokeWidth: 2,
                  },
                },
                ticks: {
                  line: {
                    strokeWidth: 0,
                  },
                },
                legend: {
                  text: {
                    stroke: '#791fd1',
                    fontSize: 16,
                    strokeWidth: 0.2,
                  },
                },
              },
            }}
          />
        </div>
      )}
    </Spacing>
  );
};

export const NlpGraphPage = () => {
  const { userAuthenticatedApiCall, featureFlags } = useUser();
  const { myObservation, selectedOrganizationId } = useObservation();

  useAuthenticatedPageRedirect();

  return (
    <MainLayout showBackToTop>
      {Boolean(featureFlags?.dangerouslyExposeNlpGraph) &&
        myObservation &&
        selectedOrganizationId && (
          <CustomViz
            observation={myObservation.type}
            organizationId={selectedOrganizationId}
            customVizDefinition={NlpGraphCustomVizDefinition}
            userAuthenticatedApiCall={userAuthenticatedApiCall}
          >
            <NlpGraphComponent
              observation={myObservation}
              organizationId={selectedOrganizationId}
            />
          </CustomViz>
        )}
    </MainLayout>
  );
};
